import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, ChevronLeft, Database, Eye, GitBranch, Menu, Plus, Search, Zap,
} from "lucide-react";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { SPEEDS } from "../../constants";
import { fmtDate, nowStr, uid } from "../../utils";
import {
  debounced, onPersistError, saveCollection, saveMatterMeta, saveUiState,
} from "../../api/client";
import { applySyncResult, reconcileCorpuses, syncCorpusToServer } from "../../services/matterSync";
import { listAllVersions, type FlatVersion } from "../../api/workbench";
import { MultiSelect } from "../common/MultiSelect";
import { LibraryView } from "../Library/LibraryView";
import { SimDirectory } from "../Workspace/SimDirectory";
import { SimView } from "../Simulation/SimView";
import { ReplayView } from "../Simulation/ReplayView";
import { EmailModal } from "../Modals/EmailModal";
import { Tour } from "../Modals/Tour";
import type {
  CompletedRun, CorpusSet, EvalSet, InitialState,
  RunStateSnapshot, SimEmail, Workspace, WorkstreamBundle, WorkstreamLite,
} from "../../types";

export interface AppShellProps {
  initial: InitialState & { firmThresholds?: Record<string, number> | null };
}

type View = "sim" | "library";
type SimViewMode = "dir" | "setup" | "workspace" | "replay";

/* AppShell — top-level layout. Owns global UI state (view, simView, libTab,
   tour, modalEmail), all library asset state with debounced persistence,
   sim-launch selection (corpus / checks / eval / bundle / matters), and the
   workspace list. Delegates the active run loop to <SimView>, completed runs
   to <ReplayView>, and asset editing to <LibraryView>. */
export function AppShell({ initial }: AppShellProps): JSX.Element {
  const ui0: Record<string, any> = (initial.ui as Record<string, any>) || {};
  const { isMobile, isCompact } = useBreakpoint();
  const [view, setView] = useState<View>((ui0.view as View) || "sim");
  const [navOpen, setNavOpen] = useState<boolean>(false);
  const [libTab, setLibTab] = useState<string>(ui0.libTab || "corpus");
  const [tour, setTour] = useState<boolean>(() => {
    try {
      return typeof window !== "undefined" && !window.localStorage.getItem("triage_tour_seen");
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!tour) {
      try {
        window.localStorage.setItem("triage_tour_seen", "1");
      } catch {
        /* ignore */
      }
    }
  }, [tour]);

  /* ---- library assets (versionable, editable, persisted) ---- */
  const [corpuses, setCorpuses] = useState<CorpusSet[]>(initial.corpuses);
  const [evalSets, setEvalSets] = useState<EvalSet[]>(initial.evalSets);
  const [wsBundles, setWsBundles] = useState<WorkstreamBundle[]>(initial.wsBundles);
  /* Saved bundle versions across all bundles, newest first — from the
     agent-bundles API (the tables are the source of truth). Refreshed when
     the simulation surface is (re)entered so registry saves show up. */
  const [bundleVersions, setBundleVersions] = useState<FlatVersion[]>([]);
  /* ---- simulation selection (the recorded config) ---- */
  const [selCorpusId, setSelCorpusId] = useState<string>(
    ui0.selCorpusId || initial.corpuses[0].id,
  );
  const [selCheckBundleId, setSelCheckBundleId] = useState<string>(ui0.selCheckBundleId || "");
  const [selCheckVerId, setSelCheckVerId] = useState<string>(ui0.selCheckVerId || "");
  const [selEvalId, setSelEvalId] = useState<string | null>(
    ui0.selEvalId !== undefined ? ui0.selEvalId : initial.evalSets[0].id,
  );
  const [selWsBundleId, setSelWsBundleId] = useState<string>(
    ui0.selWsBundleId || initial.wsBundles[0].id,
  );
  const [selMatters, setSelMatters] = useState<Set<string>>(() => {
    if (ui0.selMatters) return new Set<string>(ui0.selMatters);
    const b =
      initial.wsBundles.find((x) => x.id === (ui0.selWsBundleId || initial.wsBundles[0].id)) ||
      initial.wsBundles[0];
    return new Set<string>(b.workstreams.map((w) => w.id));
  });
  const wsBundle = wsBundles.find((b) => b.id === selWsBundleId) || wsBundles[0];
  const wsDefs: WorkstreamLite[] = wsBundle.workstreams;
  const [workspaces, setWorkspaces] = useState<Workspace[]>(initial.workspaces);
  const [currentWsId, setCurrentWsId] = useState<string | null>(ui0.currentWsId || null);

  /* in-progress run snapshots, keyed by workspace id. rs0 is the snapshot we
     reopen into when restoring an active workspace. */
  const runStatesRef = useRef<Record<string, RunStateSnapshot>>(initial.runStates || {});
  const didMountRef = useRef<boolean>(false);
  const restoredReplay: CompletedRun | null = ui0.replayWsId
    ? ((initial.workspaces.find((w) => w.id === ui0.replayWsId) || ({} as Workspace)).run ||
      null)
    : null;
  const [replayRun, setReplayRun] = useState<CompletedRun | null>(restoredReplay);
  const [simView, setSimView] = useState<SimViewMode>(() => {
    let sv: SimViewMode = (ui0.simView as SimViewMode) || "dir";
    if (sv === "replay" && !restoredReplay) sv = "dir";
    if (sv === "workspace") {
      const w = initial.workspaces.find((x) => x.id === ui0.currentWsId);
      if (!w || w.status === "completed") sv = "dir";
    }
    return sv;
  });
  const [draftName, setDraftName] = useState<string>(ui0.draftName || "");
  const [speedIdx, setSpeedIdx] = useState<number>(ui0.speedIdx ?? 1);
  const [modalEmail, setModalEmail] = useState<SimEmail | null>(null);

  /* ---- derived from current selection ---- */
  const corpus = corpuses.find((c) => c.id === selCorpusId) || corpuses[0];
  const emails = corpus.emails;
  /* Newest-first saved versions for the single setup dropdown. Drafts are not
     selectable — only an immutable saved version is runnable ("what you see is
     what runs"). */
  useEffect(() => {
    if (view !== "sim") return;
    void listAllVersions().then(setBundleVersions);
  }, [view, simView]);
  const flatVersionOpts = bundleVersions.map((v) => ({
    value: `${v.bundleId}::${v.versionId}`,
    label: `${v.bundleName} / ${v.label} · ${v.agentCount} agents · ${fmtDate(v.createdAt)}`,
    short: `${v.bundleName} / ${v.label}`,
  }));
  /* Selector validation: the bundle ID and version ID are stored together as a
     pair.  Verify the pair is still in the fetched versions list and snap to the
     newest saved version when it is not — a `<select>` whose value matches no
     option displays the first one while state stays empty, so the snap is what
     keeps the control and the recorded config in agreement.

     A stale *prior* selection (deleted bundle) also raises a visible error; an
     empty first-boot selection snaps silently, since nothing was lost.

     Never runs while a workspace or replay is open: re-pinning under an active
     run would swap the bundle the run is executing against. */
  const [bundleSelectionError, setBundleSelectionError] = useState<string | null>(null);
  useEffect(() => {
    if (!bundleVersions.length) return;
    if (simView === "workspace" || simView === "replay") return;
    const valid = bundleVersions.some(
      (v) => v.bundleId === selCheckBundleId && v.versionId === selCheckVerId,
    );
    if (valid) {
      setBundleSelectionError(null);
      return;
    }
    const hadPriorSelection = !!(selCheckBundleId || selCheckVerId);
    setSelCheckBundleId(bundleVersions[0].bundleId);
    setSelCheckVerId(bundleVersions[0].versionId);
    setBundleSelectionError(
      hadPriorSelection
        ? "The saved agent bundle version no longer exists. " +
            "Automatically selected the newest saved version — please confirm before activating."
        : null,
    );
    /* Selection deps are intentionally omitted: re-running on the snap we just made
       would clear the error before it is read. Explicit changes clear it themselves. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleVersions, simView]);
  const pinnedVersion = bundleVersions.find(
    (v) => v.bundleId === selCheckBundleId && v.versionId === selCheckVerId,
  );
  const selEval = evalSets.find((e) => e.id === selEvalId && e.corpusId === selCorpusId) || null;
  const wsNameMap = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = { unassoc: "Unassociated" };
    wsDefs.forEach((w) => {
      m[w.id] = w.name;
    });
    return m;
  }, [wsDefs]);
  const wsName = (id: string): string => wsNameMap[id] || id;
  const toggleMatter = (id: string): void =>
    setSelMatters((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const mattersInScope = wsDefs.filter((w) => selMatters.has(w.id));
  const currentWs = workspaces.find((w) => w.id === currentWsId) || null;
  const currentInitialSnap = currentWsId ? runStatesRef.current[currentWsId] || null : null;
  /* A workspace must run against ITS corpus. If that corpus is gone from the
     backend (e.g. its matter was deleted, or it never synced and a reload
     dropped it), refuse to open the run view rather than silently falling
     back to corpuses[0] and simulating the wrong emails. */
  const currentWsCorpus = currentWs?.config
    ? corpuses.find((c) => c.id === currentWs.config!.corpusId) || null
    : null;

  /* ---- navigation actions ---- */
  function openNew(): void {
    setDraftName("");
    setSimView("setup");
  }
  function activateWorkspace(): void {
    const id = "ws-" + uid();
    const cname = (corpuses.find((c) => c.id === selCorpusId) || ({} as CorpusSet)).name || "Untitled";
    const name = draftName.trim() || (cname.split(" — ")[0] + " run");
    setWorkspaces((list) => [
      {
        id,
        name,
        createdAt: nowStr(),
        status: "active",
        config: {
          corpusId: selCorpusId,
          wsBundleId: selWsBundleId,
          matters: [...selMatters],
          checkBundleId: selCheckBundleId,
          checkVerId: selCheckVerId,
          evalId: selEvalId,
          speedIdx,
        },
      },
      ...list,
    ]);
    setCurrentWsId(id);
    setSimView("workspace");
  }
  function openActive(w: Workspace): void {
    if (!w.config) return;
    if (w.config.wsBundleId) setSelWsBundleId(w.config.wsBundleId);
    setSelCorpusId(w.config.corpusId);
    setSelMatters(new Set(w.config.matters));
    setSelCheckBundleId(w.config.checkBundleId);
    setSelCheckVerId(w.config.checkVerId);
    setSelEvalId(w.config.evalId);
    setSpeedIdx(w.config.speedIdx ?? 1);
    setCurrentWsId(w.id);
    setSimView("workspace");
  }
  function openCompleted(w: Workspace): void {
    setReplayRun(w.run);
    setSimView("replay");
  }
  function backToDir(): void {
    setSimView("dir");
    setCurrentWsId(null);
    setReplayRun(null);
  }
  function onSimComplete(wsId: string, run: CompletedRun): void {
    setWorkspaces((list) =>
      list.map((w) =>
        w.id === wsId
          ? { ...w, status: "completed", completedAt: new Date().toLocaleString(), run }
          : w,
      ),
    );
    delete runStatesRef.current[wsId];
  }
  /* Re-pin the active workspace to a different check-set version from the run
     header. Updates the shared selection (re-derives checkVer for SimView) and
     writes it back into the workspace config so it persists. Swappable mid-run:
     emails processed after the change use the new bundle; SimView only blocks
     the control while an email is actively in-flight. */
  function changeCheckVer(value: string): void {
    const [bid, vid] = value.split("::");
    if (!bid || !vid) return;
    /* Validate the pair: the version must belong to the bundle as a unit.
       Options in flatVersionOpts are always valid pairs (derived from the same
       bundleVersions fetch), but a stale or manually-constructed value could
       slip through — guard it here so the server never receives a mismatched
       bundleId↔versionId pair. */
    const found = bundleVersions.find((v) => v.bundleId === bid && v.versionId === vid);
    if (!found) {
      setBundleSelectionError(
        `Bundle version not found (bundle: ${bid}, version: ${vid}). ` +
          "Refresh the page or re-open the workspace to reload available versions.",
      );
      return;
    }
    setBundleSelectionError(null);
    setSelCheckBundleId(bid);
    setSelCheckVerId(vid);
    /* Only the run header re-pins a workspace. The setup form shares this validated
       path but must not write back to whichever workspace was last opened. */
    if (!currentWsId || simView !== "workspace") return;
    setWorkspaces((list) =>
      list.map((w) =>
        w.id === currentWsId && w.config
          ? { ...w, config: { ...w.config, checkBundleId: bid, checkVerId: vid } }
          : w,
      ),
    );
  }

  /* ---- persistence: debounced writes of each slice to the backend ----
     Emails/attachments mirror through matterSync as they are edited; the
     corpus effect only persists per-matter metadata (note, log, estimate). */
  useEffect(() => {
    if (!didMountRef.current) return;
    corpuses.forEach((c) => {
      if (!c.matterId) return;
      const { matterId } = c;
      debounced(`matterMeta:${matterId}`, () =>
        void saveMatterMeta(matterId, {
          localId: c.id,
          note: c.note,
          updated: c.updated,
          log: c.log,
          lastEstimate: c.lastEstimate,
        }),
      );
    });
  }, [corpuses]);
  useEffect(() => {
    if (didMountRef.current) debounced("evalSets", () => void saveCollection("eval-sets", evalSets));
  }, [evalSets]);
  useEffect(() => {
    if (didMountRef.current) debounced("wsBundles", () => void saveCollection("ws-bundles", wsBundles));
  }, [wsBundles]);
  useEffect(() => {
    if (didMountRef.current) debounced("workspaces", () => void saveCollection("workspaces", workspaces));
  }, [workspaces]);
  useEffect(() => {
    if (!didMountRef.current) return;
    const ui = {
      view,
      libTab,
      simView,
      currentWsId,
      replayWsId: replayRun
        ? (workspaces.find((w) => w.run === replayRun) || ({} as Workspace)).id || null
        : null,
      selCorpusId,
      selCheckBundleId,
      selCheckVerId,
      selEvalId,
      selWsBundleId,
      selMatters: [...selMatters],
      speedIdx,
      draftName,
    };
    debounced("ui", () => void saveUiState("ui", ui));
  }, [
    view, libTab, simView, currentWsId, replayRun, selCorpusId, selCheckBundleId,
    selCheckVerId, selEvalId, selWsBundleId, selMatters, speedIdx, draftName, workspaces,
  ]);
  useEffect(() => {
    didMountRef.current = true;
  }, []);

  /* ---- B2: surface persistence failures ---- */
  const [persistError, setPersistError] = useState<string | null>(null);
  useEffect(() => onPersistError(setPersistError), []);

  /* ---- B1: mirror corpuses into the relational backend on load ---- */
  useEffect(() => {
    let alive = true;
    void reconcileCorpuses(initial.corpuses).then((synced) => {
      if (!alive || !Object.keys(synced).length) return;
      setCorpuses((cs) => cs.map((c) => (synced[c.id] ? applySyncResult(c, synced[c.id]) : c)));
    });
    return () => {
      alive = false;
    };
  }, []);

  /* On-demand corpus sync for live runs: a resumed run reads the current
     corpuses via a ref (not a stale prop) and imports any email still missing
     a backend serverId, then folds the ids back into state so later emails
     hit the fast path. Returns localId → serverId for every email that now has
     one. A concurrent in-flight sync is shared to avoid duplicate imports. */
  const corpusesRef = useRef(corpuses);
  corpusesRef.current = corpuses;
  const syncInFlightRef = useRef<Record<string, Promise<Record<number, string> | null>>>({});
  function ensureCorpusSynced(corpusId: string): Promise<Record<number, string> | null> {
    const cur = corpusesRef.current.find((c) => c.id === corpusId);
    if (!cur) return Promise.resolve(null);
    const map: Record<number, string> = {};
    cur.emails.forEach((e) => {
      if (e.serverId) map[e.id] = e.serverId;
    });
    if (cur.emails.every((e) => e.serverId)) return Promise.resolve(map);
    if (corpusId in syncInFlightRef.current) return syncInFlightRef.current[corpusId];
    const p = syncCorpusToServer(cur).then((r) => {
      if (!r) return null;
      setCorpuses((cs) => cs.map((c) => (c.id === corpusId ? applySyncResult(c, r) : c)));
      const full: Record<number, string> = { ...map };
      cur.emails.forEach((e) => {
        const id = e.serverId || r.serverIds[e.id];
        if (id) full[e.id] = id;
      });
      return full;
    });
    syncInFlightRef.current[corpusId] = p;
    void p.finally(() => {
      delete syncInFlightRef.current[corpusId];
    });
    return p;
  }

  return (
    <div className="ass">
      <a className="skiplink" href="#main">
        Skip to content
      </a>
      <header className="nav" role="banner">
        <div className="logo" aria-hidden="true">
          <GitBranch size={15} />
        </div>
        <span className="brandname">Triage</span>
        {!isMobile && (
          <span
            className="brandsub"
            title="Production-mirroring environment for calibrating the live system"
          >
            Simulation Environment
          </span>
        )}
        {!isMobile && (
          <nav className="navtabs" aria-label="Primary">
            <button className={view === "library" ? "on" : ""} onClick={() => setView("library")}>
              Library
            </button>
            <button className={view === "sim" ? "on" : ""} onClick={() => setView("sim")}>
              Simulation
            </button>
          </nav>
        )}
        <div className="spacer" />
        {!isMobile && (
          <>
            <button
              className="tourbtn"
              onClick={() => setTour(true)}
              aria-label="Launch product tour"
            >
              <Eye size={13} /> Tour
            </button>
            <button className="icon-btn" aria-label="Search">
              <Search size={17} />
            </button>
            <div className="avatar" aria-hidden="true">
              ET
            </div>
          </>
        )}
        {isMobile && (
          <button className="icon-btn" aria-label="Menu" onClick={() => setNavOpen(!navOpen)}>
            <Menu size={20} />
          </button>
        )}
      </header>
      {persistError && (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 16px",
            background: "var(--red-bg, #fdecea)",
            color: "var(--red, #b3261e)",
            borderBottom: "1px solid var(--bd)",
            fontSize: 12.5,
          }}
        >
          <span style={{ flex: 1 }}>{persistError}</span>
          <button className="lnk" onClick={() => setPersistError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {navOpen && isMobile && (
        <>
          <div className="backdrop" onClick={() => setNavOpen(false)} />
          <div className="menu">
            <button
              className={view === "library" ? "on" : ""}
              onClick={() => {
                setView("library");
                setNavOpen(false);
              }}
            >
              <Database size={15} /> Library
            </button>
            <button
              className={view === "sim" ? "on" : ""}
              onClick={() => {
                setView("sim");
                setNavOpen(false);
              }}
            >
              <Activity size={15} /> Simulation
            </button>
          </div>
        </>
      )}

      {view === "sim" && simView === "dir" && (
        <SimDirectory
          workspaces={workspaces}
          onNew={openNew}
          onOpenActive={openActive}
          onOpenCompleted={openCompleted}
          corpuses={corpuses}
          bundleVersions={bundleVersions}
          evalSets={evalSets}
          wsBundles={wsBundles}
          wsName={wsName}
          onLaunchTour={() => setTour(true)}
        />
      )}

      {view === "sim" && simView === "setup" && (
        <main className="page" id="main">
          <div className="content scroll">
            <div className="pagehd" style={{ padding: "24px 0 8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={backToDir}>
                  <ChevronLeft size={14} /> Simulations
                </button>
                <div>
                  <h1 className="h1">New simulation</h1>
                  <div className="psub">
                    Name it and choose the inputs, then activate a workspace.
                  </div>
                </div>
              </div>
            </div>
            <div className="card" style={{ padding: 20, maxWidth: 780 }}>
              <div className="cfgf" style={{ marginBottom: 16 }}>
                <label className="up">Workspace name</label>
                <input
                  className="inp"
                  placeholder="e.g. Lumen Series B — calibrated checks"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                />
                <div className="hint">Shown in the directory; pick something you'll recognize later.</div>
              </div>
              <div className="cfgcol">
                <div className="cfgf">
                  <label className="up">Email corpus</label>
                  <select
                    className="sel"
                    value={selCorpusId}
                    onChange={(e) => setSelCorpusId(e.target.value)}
                  >
                    {corpuses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.emails.length} emails
                      </option>
                    ))}
                  </select>
                  <div className="hint">{corpus.note}</div>
                </div>
                <div className="cfgf">
                  <label className="up">Workstream bundle</label>
                  <select
                    className="sel"
                    value={selWsBundleId}
                    onChange={(e) => {
                      const b = wsBundles.find((x) => x.id === e.target.value);
                      setSelWsBundleId(e.target.value);
                      setSelMatters(new Set((b ? b.workstreams : []).map((w) => w.id)));
                    }}
                  >
                    {wsBundles.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} · {b.workstreams.length}
                      </option>
                    ))}
                  </select>
                  <div className="hint">
                    {wsBundle.note} — should match the corpus's matters.
                  </div>
                </div>
                <div className="cfgf">
                  <label className="up">Matters in scope</label>
                  <MultiSelect
                    options={wsDefs.map((w) => ({ id: w.id, label: w.name }))}
                    selected={selMatters}
                    onToggle={toggleMatter}
                    allLabel={"All in bundle (" + wsDefs.length + ")"}
                  />
                  <div className="hint">
                    Subset of the bundle to catalog against; off-scope mail → Unassociated.
                  </div>
                </div>
                <div className="cfgf">
                  <label className="up">Agent bundle</label>
                  {bundleSelectionError && (
                    <div
                      role="alert"
                      style={{
                        padding: "6px 10px",
                        marginBottom: 6,
                        background: "var(--red-bg, #fdecea)",
                        color: "var(--red, #b3261e)",
                        borderRadius: 4,
                        fontSize: 12,
                        lineHeight: 1.4,
                      }}
                    >
                      {bundleSelectionError}
                    </div>
                  )}
                  <select
                    className="sel"
                    value={`${selCheckBundleId}::${selCheckVerId}`}
                    /* Same validated path as the run header, so the banner above this
                       control reflects the guarantee it advertises. */
                    onChange={(e) => changeCheckVer(e.target.value)}
                  >
                    {flatVersionOpts.length === 0 && (
                      <option value="">No saved versions — save one in the Agent Registry</option>
                    )}
                    {flatVersionOpts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <div className="hint">
                    Saved versions across all bundles, newest first. Drafts aren't selectable — save
                    a version to run it.
                  </div>
                </div>
                <div className="cfgf">
                  <label className="up">
                    Eval set{" "}
                    <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--tert)" }}>
                      · optional
                    </span>
                  </label>
                  <select
                    className="sel"
                    value={selEvalId || ""}
                    onChange={(e) => setSelEvalId(e.target.value || null)}
                  >
                    <option value="">No eval set</option>
                    {evalSets
                      .filter((s) => s.corpusId === selCorpusId)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </select>
                  <div className="hint">
                    Scores actual output against an answer key after the run.
                  </div>
                </div>
              </div>
              <div className="cfgbar">
                <div className="cfgf" style={{ maxWidth: 220 }}>
                  <label className="up">Playback speed</label>
                  <div
                    style={{
                      display: "flex",
                      border: "1px solid var(--bd2)",
                      borderRadius: 6,
                      overflow: "hidden",
                      width: "fit-content",
                    }}
                  >
                    {SPEEDS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setSpeedIdx(i)}
                        className="mono"
                        title={s[0] === "⚡" ? "Instant — skip playback delay" : s[0] + " playback speed"}
                        style={{
                          padding: "7px 12px",
                          fontSize: 12,
                          display: "inline-flex",
                          alignItems: "center",
                          background: speedIdx === i ? "var(--fg)" : "transparent",
                          color: speedIdx === i ? "#fff" : "var(--muted)",
                        }}
                      >
                        {s[0] === "⚡" ? <Zap size={13} /> : s[0]}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="spacer" />
                <button
                  className="btn btn-primary"
                  onClick={activateWorkspace}
                  disabled={mattersInScope.length === 0 || emails.length === 0}
                >
                  <Plus size={15} /> Activate workspace
                </button>
              </div>
            </div>
          </div>
        </main>
      )}

      {view === "sim" && simView === "workspace" && currentWs && !currentWsCorpus && (
        <main className="page" id="main">
          <div className="content scroll">
            <div className="pagehd" style={{ padding: "24px 0 8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={backToDir}>
                  <ChevronLeft size={14} /> Simulations
                </button>
                <h1 className="h1">{currentWs.name}</h1>
              </div>
            </div>
            <div className="card" style={{ padding: 20, maxWidth: 640 }} role="alert">
              <div style={{ color: "var(--red)", fontWeight: 600, marginBottom: 8 }}>
                This simulation&apos;s corpus is missing from the backend.
              </div>
              <div className="psub" style={{ lineHeight: 1.5 }}>
                The workspace is pinned to a corpus that no longer exists on the server — its
                matter may have been deleted, or the corpus never finished syncing before a
                reload. Running it now would simulate the wrong emails, so the run view is
                disabled. Re-import the corpus in the Library, or create a new simulation.
              </div>
            </div>
          </div>
        </main>
      )}

      {view === "sim" && simView === "workspace" && currentWs && currentWsCorpus && (
        <SimView
          key={currentWs.id}
          workspace={currentWs}
          corpus={currentWsCorpus}
          pinned={{
            bundleId: selCheckBundleId,
            versionId: selCheckVerId,
            label: pinnedVersion ? `${pinnedVersion.bundleName} / ${pinnedVersion.label}` : selCheckVerId,
          }}
          versionOpts={flatVersionOpts}
          selVer={`${selCheckBundleId}::${selCheckVerId}`}
          onChangeCheckVer={changeCheckVer}
          selEval={selEval}
          mattersInScope={mattersInScope}
          wsName={wsName}
          wsNameMap={wsNameMap}
          initialSnapshot={currentInitialSnap}
          speedIdx={speedIdx}
          setSpeedIdx={setSpeedIdx}
          onBack={backToDir}
          onComplete={onSimComplete}
          onEnsureSynced={() => ensureCorpusSynced(currentWsCorpus.id)}
        />
      )}

      {view === "sim" && simView === "replay" && replayRun && (
        <ReplayView run={replayRun} onBack={backToDir} isCompact={isCompact} />
      )}

      {view === "library" && (
        <LibraryView
          tab={libTab}
          setTab={setLibTab}
          isMobile={isMobile}
          corpuses={corpuses}
          setCorpuses={setCorpuses}
          evalSets={evalSets}
          setEvalSets={setEvalSets}
          wsBundles={wsBundles}
          setWsBundles={setWsBundles}
        />
      )}

      {modalEmail && <EmailModal e={modalEmail} onClose={() => setModalEmail(null)} />}
      {tour && (
        <Tour
          onClose={() => setTour(false)}
          goSim={() => {
            setView("sim");
            setSimView("dir");
          }}
          goLibrary={() => {
            setView("library");
            setLibTab("corpus");
          }}
          workspaces={workspaces}
        />
      )}
    </div>
  );
}

